// Kinio identity（app/home-base docs/KINIO-API.md §1）：login / signup / refresh / logout / me。
//
// Token 存放可換（setSessionPersistence）：
//   - 預設 localStoragePersistence（home-base web：web 沒有 Keychain，契約 §3.1「memory+refresh cookie 不適用 → localStorage 先」）。
//   - memoryPersistence：只放記憶體；VMS 的 refresh token 在 BFF 的 HttpOnly cookie（plan/vms-web-v0.md §3.1）。
// refresh / logout 的傳輸也可換（setAuthTransport）：預設直打 identity，VMS 改打自己的 BFF。
// access 1h、refresh 30 天 **rotation**（用一次就換，舊的立刻失效 → 存新的）。
//
// 規則：`Authorization: Bearer <access>` 打 identity 與各 plane api；401 → refresh 一次再
// 重試；refresh 也失敗 → 清 session（使用端訂閱 session 變化 → 登出畫面）。
// refresh 單飛：多個 401 併發共用同一個 in-flight refresh。
// refresh 的兩個特例（identity 66f94e7）：401 CLIENT_REVOKED（這版 App 停用）→ 清 session + authNotice
// 'client_revoked'（登入頁顯示「請更新」）；403 ECOSYSTEM_SUSPENDED → **保留 session**、authNotice
// 'ecosystem_suspended'（服務暫停，恢復後照常換 token）。
// 403 REAUTH_REQUIRED：有 setStepUpHandler 就跳重新驗證後重試一次（VMS），沒有就拋出給呼叫端（home-base 自己處理）。
//
// 條款（66f94e7）：token 回應與 /v1/me 帶 memberships + tos_required；存進 session，使用端顯示並
// acceptTerms()。不擋登入，所以也不再有 409 JOIN_BRAND_REQUIRED / join_brand 重送。

import { identityUrl, kinioConfig } from './config';
import { detectDeviceLabel } from './deviceLabel';
import { KinioError, errorFromResponse } from './errors';
import type { BrandMembership, KinioHome, KinioPlanes, KinioUser, LoginResponse, MeResponse, TosRequired } from './types';
import { appUpgrade, noteUpgradeRequired } from './upgrade';

// ---- SDK 識別（SPEC #public-api C4）：SDK 發出的每個請求都帶 `X-Kinio-SDK: web/<semver>` ----
// 版本 = kinioConfig.appVersion（使用端注入）；沒注入 → 不帶這個標頭（vms 的 Caddy CORS 有放行，但它還沒報版本）。

export const SDK_HEADER = 'X-Kinio-SDK';
const SEMVER_RE = /^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.+-]*)?$/;

/** App 對外版本正規化成 semver（去掉前綴 v 與空白；不是 semver → 0.0.0）。 */
export function sdkVersion(raw: string = kinioConfig.appVersion): string {
  const v = raw.trim().replace(/^v/i, '');
  return SEMVER_RE.test(v) ? v : '0.0.0';
}

/** `X-Kinio-SDK` 標頭（identity 與 plane api 的請求共用）；沒注入 appVersion → {}。 */
export function sdkHeaders(): Record<string, string> {
  return kinioConfig.appVersion ? { [SDK_HEADER]: `web/${sdkVersion()}` } : {};
}

// ---- 本機診斷記錄（使用端注入；home-base：支援單「附上 App 記錄」）----

export type DiagnosticLogger = (level: 'info' | 'warn' | 'error', message: string) => void;
let diagLog: DiagnosticLogger | null = null;

/** 注入診斷記錄；SDK 只記方法、路徑（不含 query）、狀態與錯誤碼。 */
export function setDiagnosticLogger(fn: DiagnosticLogger | null): void {
  diagLog = fn;
}

export const SESSION_STORAGE_KEY = 'kinio.session.v1';

export interface KinioSession {
  access_token: string;
  refresh_token: string;
  /** access token 到期（epoch ms）；未知 → 0。 */
  expires_at: number;
  user: KinioUser | null;
  homes: KinioHome[];
  memberships?: BrandMembership[];
  /** 非 null = 這個 App 品牌的條款要同意（TermsGate）。 */
  tos_required?: TosRequired | null;
  /** identity 回的「可建家戶的 plane」（`{env: {api,…}}`；可能沒有）。me/devices 從這些 plane 與 homes[].endpoints.api 拉。 */
  planes?: KinioPlanes;
}

type SessionListener = (s: KinioSession | null) => void;

let current: KinioSession | null | undefined; // undefined = 尚未從 storage 讀
const listeners = new Set<SessionListener>();

/** session 放哪裡。read 回 null = 沒有 session。 */
export interface SessionPersistence {
  read(): KinioSession | null;
  write(s: KinioSession | null): void;
}

function parseSession(raw: string | null | undefined): KinioSession | null {
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Partial<KinioSession>;
    if (typeof s.access_token !== 'string' || typeof s.refresh_token !== 'string') return null;
    return {
      access_token: s.access_token,
      refresh_token: s.refresh_token,
      expires_at: typeof s.expires_at === 'number' ? s.expires_at : 0,
      user: s.user ?? null,
      homes: Array.isArray(s.homes) ? s.homes : [],
      memberships: Array.isArray(s.memberships) ? s.memberships : undefined,
      tos_required: s.tos_required ?? null,
      ...(s.planes && typeof s.planes === 'object' ? { planes: s.planes } : {}),
    };
  } catch {
    return null;
  }
}

/** localStorage（隱私模式 / 無 storage 時只留記憶體）。 */
export const localStoragePersistence: SessionPersistence = {
  read() {
    try {
      return parseSession(globalThis.localStorage?.getItem(SESSION_STORAGE_KEY));
    } catch {
      return null;
    }
  },
  write(s) {
    try {
      if (s) globalThis.localStorage?.setItem(SESSION_STORAGE_KEY, JSON.stringify(s));
      else globalThis.localStorage?.removeItem(SESSION_STORAGE_KEY);
    } catch {
      /* 只留記憶體 */
    }
  },
};

/** 只放記憶體（重新整理即消失；使用端靠 BFF cookie 重新換 access token）。 */
export const memoryPersistence: SessionPersistence = {
  read: () => null,
  write: () => {},
};

let persistence: SessionPersistence = localStoragePersistence;

/** 換 session 存放方式；會丟掉目前的記憶體快取。 */
export function setSessionPersistence(p: SessionPersistence): void {
  persistence = p;
  current = undefined;
}

function readStorage(): KinioSession | null {
  return persistence.read();
}

function writeStorage(s: KinioSession | null): void {
  persistence.write(s);
}

export const session = {
  get(): KinioSession | null {
    if (current === undefined) current = readStorage();
    return current;
  },
  set(s: KinioSession | null): void {
    current = s;
    writeStorage(s);
    for (const fn of listeners) fn(s);
  },
  /** 部分更新（例如 /v1/me 回來的 user/homes）。沒有 session 時 no-op。 */
  patch(p: Partial<KinioSession>): void {
    const cur = session.get();
    if (!cur) return;
    session.set({ ...cur, ...p });
  },
  clear(): void {
    session.set(null);
  },
  /** 丟掉記憶體快取，下次 get() 重新從 storage 讀（重新整理語意；測試用）。 */
  reload(): void {
    current = undefined;
  },
  subscribe(fn: SessionListener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  accessToken(): string | null {
    return session.get()?.access_token ?? null;
  },
};

/** 把 login 形狀的回應（login / mfa verify / cancel-deletion）寫成目前 session。沒有 token → 拋 BAD_LOGIN_RESPONSE。 */
export function startSession(data: LoginResponse, what = 'login'): LoginResponse {
  if (!data?.access_token || !data.refresh_token) throw new KinioError(502, 'BAD_LOGIN_RESPONSE', `${what}: no tokens`);
  authNotices.set(null); // 新登入：之前的「版本已停用 / 服務暫停」提示作廢
  appUpgrade.fromTokenResponse(data);
  session.set(sessionFromLogin(data));
  return data;
}

function sessionFromLogin(data: LoginResponse): KinioSession {
  const ttl = typeof data.expires_in === 'number' && data.expires_in > 0 ? data.expires_in : 3600;
  return {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    expires_at: Date.now() + ttl * 1000,
    user: data.user ?? null,
    homes: Array.isArray(data.homes) ? data.homes : [],
    memberships: Array.isArray(data.memberships) ? data.memberships : undefined,
    tos_required: data.tos_required ?? null,
    ...(data.planes && typeof data.planes === 'object' ? { planes: data.planes } : {}),
  };
}

// ---- 跨畫面的帳號狀態提示（refresh 的 CLIENT_REVOKED / ECOSYSTEM_SUSPENDED）----

export type AuthNotice = 'client_revoked' | 'ecosystem_suspended';
let notice: AuthNotice | null = null;
const noticeListeners = new Set<(n: AuthNotice | null) => void>();

export const authNotices = {
  get(): AuthNotice | null {
    return notice;
  },
  set(n: AuthNotice | null): void {
    if (notice === n) return;
    notice = n;
    for (const fn of noticeListeners) fn(n);
  },
  subscribe(fn: (n: AuthNotice | null) => void): () => void {
    noticeListeners.add(fn);
    return () => noticeListeners.delete(fn);
  },
};

/** 公開（或自帶 Bearer）的 JSON POST；不走 refresh。identity 的忘記密碼 / 重設 / 驗證信用。 */
export async function postJson<T>(url: string, body: unknown, bearer?: string | null): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...sdkHeaders() };
  if (bearer) headers['Authorization'] = 'Bearer ' + bearer;
  let res: Response;
  try {
    res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
  } catch (e) {
    throw KinioError.network(e);
  }
  if (!res.ok) {
    const err = await errorFromResponse(res);
    noteUpgradeRequired(err); // 登入 / 註冊 / MFA / refresh 的 426 → 全域強制更新畫面
    throw err;
  }
  return (await res.json()) as T;
}

export interface LoginOptions {
  /** 覆寫品牌（預設 kinioConfig.brandId）。identity 以 client_id 判品牌，brand 只是相容欄位。 */
  brand?: string;
}

/**
 * POST /v1/auth/login。成功後寫入 session。第一次用這個品牌 / 條款改版不擋登入（回應帶 tos_required），
 * 所以沒有 409 JOIN_BRAND_REQUIRED 重送（identity 66f94e7）。
 */
export async function login(email: string, password: string, opts: LoginOptions = {}): Promise<LoginResponse> {
  const brand = opts.brand ?? kinioConfig.brandId;
  const data = await postJson<LoginResponse>(identityUrl('/v1/auth/login'), { brand, client_id: kinioConfig.clientId, email, password, ...deviceLabelField() });
  return startSession(data, 'login');
}

/** 登入裝置標籤（工作階段清單顯示用，例「Chrome on macOS」）；UA 認不出來就不帶這個 key。 */
function deviceLabelField(): { device_label?: string } {
  const label = detectDeviceLabel(typeof navigator !== 'undefined' ? navigator.userAgent : '');
  return label ? { device_label: label } : {};
}

/** POST /v1/auth/signup（login 同形 + display_name）。成功即登入。 */
export async function signup(email: string, password: string, displayName: string, opts: LoginOptions = {}): Promise<LoginResponse> {
  const brand = opts.brand ?? kinioConfig.brandId;
  const data = await postJson<LoginResponse>(identityUrl('/v1/auth/signup'), {
    brand,
    client_id: kinioConfig.clientId,
    email,
    password,
    display_name: displayName,
    ...deviceLabelField(),
  });
  return startSession(data, 'signup');
}

// ---- 兩步驟驗證（登入第二步）----

/** login 401 MFA_REQUIRED 的內容：mfa_token（短效，只能換一次 token）+ 可用方式（totp / recovery）。 */
export interface MfaChallenge {
  mfa_token: string;
  methods: string[];
}

/** 從 login 的錯誤取出兩步驟驗證挑戰；不是 MFA_REQUIRED（或沒帶 mfa_token）→ null。 */
export function mfaChallengeOf(e: unknown): MfaChallenge | null {
  if (!(e instanceof KinioError) || e.code !== 'MFA_REQUIRED') return null;
  const d = e.detail;
  if (typeof d.mfa_token !== 'string' || !d.mfa_token) return null;
  return { mfa_token: d.mfa_token, methods: Array.isArray(d.methods) ? d.methods.filter((m): m is string => typeof m === 'string') : [] };
}

/** 驗證碼正規化：去掉所有空白（「123 456」、貼上的備援碼前後空白）。備援碼的 `-` 伺服器自己會忽略。 */
export function normalizeMfaCode(code: string): string {
  return code.replace(/\s+/g, '');
}

/**
 * POST /v1/auth/mfa/verify {mfa_token, code}（無 Bearer）→ 同 login 形狀。code = 6 碼 TOTP 或備援碼（用掉就作廢）。
 * 錯碼 401 MFA_CODE_INVALID；mfa_token 逾時 401 MFA_TOKEN_INVALID（要重新輸入密碼）；太多次 429。成功後寫入 session。
 */
export async function verifyMfaLogin(mfaToken: string, code: string): Promise<LoginResponse> {
  const data = await postJson<LoginResponse>(identityUrl('/v1/auth/mfa/verify'), { mfa_token: mfaToken, code: normalizeMfaCode(code) });
  return startSession(data, 'mfa verify');
}

// ---- refresh / logout 傳輸（可換）----

/** refresh 回 login 同形（至少 access_token + refresh_token）；logout best-effort。 */
export interface AuthTransport {
  refresh(cur: KinioSession): Promise<LoginResponse>;
  logout(cur: KinioSession): Promise<void>;
}

/** 預設：直打 identity。 */
export const identityTransport: AuthTransport = {
  refresh: (cur) => postJson<LoginResponse>(identityUrl('/v1/auth/refresh'), { refresh_token: cur.refresh_token }),
  logout: async (cur) => {
    await postJson(identityUrl('/v1/auth/logout'), { refresh_token: cur.refresh_token }, cur.access_token);
  },
};

let transport: AuthTransport = identityTransport;

export function setAuthTransport(t: AuthTransport): void {
  transport = t;
}

/** 由使用端（例如 BFF 登入後）直接建立 session。 */
export function adoptLogin(data: LoginResponse): KinioSession {
  if (!data?.access_token || !data.refresh_token) throw new KinioError(502, 'BAD_LOGIN_RESPONSE', 'adopt: no tokens');
  const s = sessionFromLogin(data);
  session.set(s);
  return s;
}

// ---- refresh（單飛 + rotation）----

let refreshing: Promise<string> | null = null;

/**
 * POST /v1/auth/refresh → 同 login 形狀；舊 refresh 立刻失效，存新的。
 * 失敗（401 等）→ 清 session 並拋出；網路錯誤 → 保留 session（下次再試）。
 */
export function refresh(): Promise<string> {
  if (refreshing) return refreshing;
  const cur = session.get();
  if (!cur) return Promise.reject(new KinioError(401, 'NO_SESSION', 'no session to refresh'));
  const usedRefresh = cur.refresh_token;
  refreshing = (async () => {
    let data: LoginResponse;
    try {
      data = await transport.refresh(cur);
    } catch (e) {
      if (e instanceof KinioError && e.code === 'ECOSYSTEM_SUSPENDED') {
        // 服務暫停：refresh token 沒被燒，恢復後照常換 → 保留 session。
        authNotices.set('ecosystem_suspended');
      } else if (e instanceof KinioError && e.code === 'UPGRADE_REQUIRED') {
        // 426（版本低於 force_upgrade_below）：refresh token 不作廢，更新後照常換 → 保留 session（postJson 已標必須更新）。
      } else if (e instanceof KinioError && e.status > 0 && e.status < 500) {
        // refresh 被拒（旋轉過 / 過期 / 撤銷 / 這版 App 停用）→ session 結束。
        if (e.code === 'CLIENT_REVOKED') authNotices.set('client_revoked');
        if (session.get()?.refresh_token === usedRefresh) session.clear();
      }
      throw e;
    }
    if (authNotices.get() === 'ecosystem_suspended') authNotices.set(null);
    appUpgrade.fromTokenResponse(data);
    if (!data?.access_token || !data.refresh_token) throw new KinioError(502, 'BAD_REFRESH_RESPONSE', 'refresh: no tokens');
    const live = session.get();
    // 期間已登出 → 不把新 token 寫回一個已結束的 session。
    if (!live || live.refresh_token !== usedRefresh) throw new KinioError(401, 'SESSION_ENDED', 'session ended during refresh');
    session.set({
      ...sessionFromLogin(data),
      user: data.user ?? live.user,
      homes: Array.isArray(data.homes) && data.homes.length > 0 ? data.homes : live.homes,
      memberships: Array.isArray(data.memberships) ? data.memberships : live.memberships,
      tos_required: data.tos_required !== undefined ? data.tos_required : (live.tos_required ?? null),
      ...(data.planes && typeof data.planes === 'object' ? { planes: data.planes } : live.planes ? { planes: live.planes } : {}),
    });
    return data.access_token;
  })();
  refreshing.then(
    () => {
      refreshing = null;
    },
    () => {
      refreshing = null;
    },
  );
  return refreshing;
}

/** POST /v1/auth/logout（best-effort），然後清 session。 */
export async function logout(): Promise<void> {
  const cur = session.get();
  session.clear();
  if (!cur) return;
  try {
    await transport.logout(cur);
  } catch {
    /* 網路 / 已失效都無所謂：本地 session 已清 */
  }
}

// ---- step-up（敏感操作要 5 分鐘內重新驗證，plan/vms-web-v0.md AUTH-05）----

/**
 * 伺服器回 403 `REAUTH_REQUIRED` 時呼叫：使用端跳出重新驗證（密碼 + 驗證碼），成功後用
 * `adoptAccessToken` 換上新的 access token 再 resolve(true)；使用者取消 → resolve(false)。
 * 沒設定 → 直接拋 403。
 */
export type StepUpHandler = () => Promise<boolean>;

let stepUpHandler: StepUpHandler | null = null;
let steppingUp: Promise<boolean> | null = null;

export function setStepUpHandler(h: StepUpHandler | null): void {
  stepUpHandler = h;
}

/** 換上新的 access token（重新驗證的結果；refresh 不變）。沒有 session 時 no-op。 */
export function adoptAccessToken(accessToken: string, expiresIn?: number): void {
  const cur = session.get();
  if (!cur || !accessToken) return;
  const ttl = typeof expiresIn === 'number' && expiresIn > 0 ? expiresIn : 3600;
  session.set({ ...cur, access_token: accessToken, expires_at: Date.now() + ttl * 1000 });
}

/**
 * 主動要求重新驗證（例如走 BFF、不是經 kinioFetch 的請求拿到 REAUTH_REQUIRED）。
 * 同時多個請求要重驗 → 共用一個對話框。沒設定 handler → false。
 */
export function requestStepUp(): Promise<boolean> {
  return stepUp();
}

function stepUp(): Promise<boolean> {
  if (!stepUpHandler) return Promise.resolve(false);
  if (!steppingUp) {
    steppingUp = stepUpHandler().finally(() => {
      steppingUp = null;
    });
  }
  return steppingUp;
}

// ---- 帶 Bearer 的通用 fetch（401 → refresh → 重試一次；403 REAUTH_REQUIRED → step-up → 重試一次）----

/**
 * identity 在「token 有效、但這個動作要重新驗證」時回的 401 錯誤碼（/v1/me/export、/v1/me/delete）。
 * 這些不是 session 失效 → kinioFetch 不走 refresh、也不清 session。
 */
export const REAUTH_401_CODES: ReadonlySet<string> = new Set(['INVALID_CREDENTIALS', 'MFA_REQUIRED', 'MFA_CODE_INVALID', 'REAUTH_REQUIRED']);

/**
 * 組織層的 401（token 有效，但進這個組織要先補驗證；plan SPEC #vms-web-sso-gate）：refresh 也不會過，不 refresh、不清 session
 * （不登出整個帳號），交給畫面引導——ORG_SSO_REQUIRED（detail.sso_login_url）、SESSION_REAUTH 的 detail.reason = sso_scoped
 * （SSO 登入的工作階段碰到範圍外的資源，要用自己的密碼 / passkey 再驗證一次）。
 */
export function isOrgGate401(err: KinioError): boolean {
  return err.code === 'ORG_SSO_REQUIRED' || (err.code === 'SESSION_REAUTH' && err.detail?.reason === 'sso_scoped');
}

export interface KinioFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** 內部：已經 step-up 過一次（再 403 就不再跳）。 */
  _steppedUp?: boolean;
  /** 內部：429 RATE_LIMITED 已經重試幾次。 */
  _rateLimitRetries?: number;
}

const RATE_LIMIT_MAX_RETRIES = 3;
const RATE_LIMIT_MAX_WAIT_S = 10;

/** 限流重試的等待（測試換掉，不真的等）；signal 中止 → 提早結束。 */
let rateLimitSleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => (clearTimeout(t), resolve()), { once: true });
  });

/** 測試用：換掉限流重試的等待。 */
export function setRateLimitSleep(fn: (ms: number, signal?: AbortSignal) => Promise<void>): void {
  rateLimitSleep = fn;
}

/**
 * GET 遇 429 `RATE_LIMITED`（或 body 沒有錯誤碼的 429）要等幾毫秒再送；不重試 → null。
 * 照 `detail.retry_after_seconds` / `Retry-After`，沒給就 1、2、4 秒退避，再多等 0–20% 亂數（同時被擋的一批請求不會同一刻再撞）。
 * 只重試 GET；伺服器要等超過 10 秒或已重試 3 次 → 不重試（畫面顯示忙碌 + 手動重試）。SPEC #eng-10 輪詢與限流、PAPI-15。
 */
function rateLimitWaitMs(err: KinioError, opts: KinioFetchOptions): number | null {
  const attempt = opts._rateLimitRetries ?? 0;
  if ((opts.method ?? 'GET') !== 'GET' || err.status !== 429 || (err.code !== 'RATE_LIMITED' && err.code !== 'HTTP_429')) return null;
  if (attempt >= RATE_LIMIT_MAX_RETRIES) return null;
  const s = err.retryAfterS ?? 2 ** attempt;
  if (s > RATE_LIMIT_MAX_WAIT_S) return null;
  const ms = Math.max(0, s) * 1000;
  return ms + Math.floor(ms * Math.random() * 0.2);
}

/**
 * 對 identity 或 plane api 發 Bearer 請求，回傳原始 `Response`（成功時）。
 * 401 → refresh 一次再重試；重試仍 401 或 refresh 失敗 → 清 session + 拋 401。
 * 非 2xx → 拋 `KinioError`。
 */
export async function kinioFetchResponse(url: string, opts: KinioFetchOptions = {}, _retried = false): Promise<Response> {
  const token = session.accessToken();
  if (!token) throw new KinioError(401, 'NO_SESSION', 'not signed in');
  const headers: Record<string, string> = { Authorization: 'Bearer ' + token, ...sdkHeaders(), ...opts.headers };
  // FormData（工單附件 multipart）原樣送，Content-Type 由瀏覽器帶 boundary。
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  // Blob（預置點縮圖 JPEG 等二進位）原樣送，Content-Type 由呼叫端的 headers 帶。
  const isBlob = typeof Blob !== 'undefined' && opts.body instanceof Blob;
  if (opts.body !== undefined && !isForm && !isBlob) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : isBlob ? (opts.body as Blob) : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch (e) {
    throw KinioError.network(e);
  }
  if (res.status === 401) {
    const err = await errorFromResponse(res);
    // 重新驗證失敗（密碼錯 / 要驗證碼…）也是 401，但 token 本身沒問題：不 refresh、不清 session。
    if (REAUTH_401_CODES.has(err.code) || isOrgGate401(err)) throw err;
    if (_retried) {
      session.clear();
      throw err;
    }
    try {
      await refresh();
    } catch (e) {
      if (e instanceof KinioError && (e.code === 'NETWORK' || e.code === 'ECOSYSTEM_SUSPENDED' || e.code === 'UPGRADE_REQUIRED')) throw e; // 保留 session
      session.clear();
      throw new KinioError(401, 'UNAUTHORIZED', 'session expired');
    }
    return kinioFetchResponse(url, opts, true);
  }
  if (!res.ok) {
    const err = await errorFromResponse(res);
    // 本機診斷記錄（支援單「附上 App 記錄」的內容）：只記方法、路徑（不含 query）、狀態與錯誤碼。
    diagLog?.('warn', `api ${opts.method ?? 'GET'} ${pathOnly(url)} status=${res.status} code=${err.code}`);
    if (res.status === 403 && err.code === 'REAUTH_REQUIRED' && !opts._steppedUp) {
      if (await stepUp()) return kinioFetchResponse(url, { ...opts, _steppedUp: true }, _retried);
    }
    const wait = rateLimitWaitMs(err, opts);
    if (wait !== null) {
      await rateLimitSleep(wait, opts.signal);
      if (!opts.signal?.aborted) return kinioFetchResponse(url, { ...opts, _rateLimitRetries: (opts._rateLimitRetries ?? 0) + 1 }, _retried);
    }
    throw err;
  }
  return res;
}

function pathOnly(url: string): string {
  const q = url.indexOf('?');
  return q < 0 ? url : url.slice(0, q);
}

/** `kinioFetchResponse` + JSON 解析（204 / 空 body → undefined）。 */
export async function kinioFetch<T>(url: string, opts: KinioFetchOptions = {}): Promise<T> {
  const res = await kinioFetchResponse(url, opts);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** `kinioFetchResponse` + 純文字（錄影 playlist.m3u8 不是 JSON）。 */
export async function kinioFetchText(url: string, opts: KinioFetchOptions = {}): Promise<string> {
  const res = await kinioFetchResponse(url, opts);
  return res.status === 204 ? '' : await res.text();
}

/** GET /v1/me（Bearer）→ user + homes + memberships + tos_required；同步回 session。 */
export async function me(): Promise<MeResponse> {
  const data = await kinioFetch<MeResponse>(identityUrl('/v1/me'));
  const cur = session.get();
  session.patch({
    user: data.user ?? cur?.user ?? null,
    homes: Array.isArray(data.homes) ? data.homes : [],
    memberships: Array.isArray(data.memberships) ? data.memberships : cur?.memberships,
    tos_required: data.tos_required !== undefined ? data.tos_required : (cur?.tos_required ?? null),
    ...(data.planes && typeof data.planes === 'object' ? { planes: data.planes } : {}),
  });
  return data;
}

/**
 * POST /v1/auth/reauth {password, code?}（Bearer）→ 新的 access token（auth_time = 現在；refresh 不換）。
 * 設定 TOTP 等「5 分鐘內驗證過」的動作前呼叫（403 REAUTH_REQUIRED）。密碼錯 401 INVALID_CREDENTIALS、
 * 開了 TOTP 沒帶碼 401 MFA_REQUIRED（都不當 session 失效）。
 */
export async function reauth(password: string, code?: string): Promise<void> {
  const body: Record<string, string> = { password };
  const c = normalizeMfaCode(code ?? '');
  if (c) body.code = c;
  const data = await kinioFetch<Partial<LoginResponse>>(identityUrl('/v1/auth/reauth'), { method: 'POST', body });
  if (!data?.access_token) throw new KinioError(502, 'BAD_REAUTH_RESPONSE', 'reauth: no access token');
  const ttl = typeof data.expires_in === 'number' && data.expires_in > 0 ? data.expires_in : 3600;
  const cur = session.get();
  session.patch({
    access_token: data.access_token,
    // 伺服器沒換 refresh（omitempty）；萬一有給就用新的
    ...(data.refresh_token ? { refresh_token: data.refresh_token } : {}),
    expires_at: Date.now() + ttl * 1000,
    user: data.user ?? cur?.user ?? null,
    ...(data.tos_required !== undefined ? { tos_required: data.tos_required } : {}),
  });
}

/**
 * POST /v1/me/brands/{brand}/accept-terms {tos_version} → {membership, memberships, tos_required}。
 * 409 TOS_VERSION_STALE（顯示的是舊版）→ 把新版寫回 session.tos_required 後拋出，讓 UI 用新版重顯。
 */
export async function acceptTerms(brand: string, tosVersion: string): Promise<void> {
  try {
    const res = await kinioFetch<{ memberships?: BrandMembership[]; tos_required?: TosRequired | null }>(
      identityUrl(`/v1/me/brands/${encodeURIComponent(brand)}/accept-terms`),
      { method: 'POST', body: { tos_version: tosVersion } },
    );
    session.patch({
      tos_required: res?.tos_required ?? null,
      ...(Array.isArray(res?.memberships) ? { memberships: res.memberships } : {}),
    });
  } catch (e) {
    if (e instanceof KinioError && e.code === 'TOS_VERSION_STALE' && typeof e.detail.current_tos_version === 'string') {
      const prev = session.get()?.tos_required;
      session.patch({
        tos_required: {
          brand: typeof e.detail.brand === 'string' ? e.detail.brand : brand,
          current_tos_version: e.detail.current_tos_version,
          url: typeof e.detail.url === 'string' ? e.detail.url : prev?.url,
          accepted_tos_version: prev?.accepted_tos_version,
        },
      });
    }
    throw e;
  }
}
